import { createElement } from 'react';
import { Routes, Route, useLocation, useNavigate, useParams } from 'react-router-dom';
import { type ContextualNavSection, type ContextualNavEntry } from '@vnetwork/ui/layout';
import { useSetContextualNav } from '@/components/layout/nav-context';
import { categoryBackTarget } from '../categories';
import { WAAP_NAV, waapHref, waapActiveId } from './waap-console-nav';
import { WaapWebsiteProvider } from './waap-context';

// WAAP lives under the Security category landing — send "Back" there.
const WAAP_BACK = categoryBackTarget('security');
import { WebsitesTab } from './website/WebsitesTab';
import { AddWebsite } from './website/AddWebsite';
import { GeneralProtectionTab } from './aiwaf/GeneralProtectionTab';
import { CustomPolicyForm } from './aiwaf/CustomPolicyForm';
import { AdvancedProtectionTab } from './aiwaf/advanced-protection/AdvancedProtectionTab';
import { AntiLeechForm } from './aiwaf/advanced-protection/AntiLeechForm';
import { PageDefacementForm } from './aiwaf/advanced-protection/PageDefacementForm';
import { CsrfForm } from './aiwaf/advanced-protection/CsrfForm';
import { BruteForceForm } from './aiwaf/advanced-protection/BruteForceForm';
import { CCProtectionForm } from './aiwaf/advanced-protection/CCProtectionForm';
import { XMLProtectionForm } from './aiwaf/advanced-protection/XMLProtectionForm';
import { WeakPasswordForm } from './aiwaf/advanced-protection/WeakPasswordForm';
import { VulnerabilityScanningForm } from './aiwaf/advanced-protection/VulnerabilityScanningForm';
import { HoneypotForm } from './aiwaf/advanced-protection/HoneypotForm';
import { CookieForm } from './aiwaf/advanced-protection/CookieForm';
import { IllegalDownloadForm } from './aiwaf/advanced-protection/IllegalDownloadForm';
import { ComplianceValidationTab } from './aiwaf/compliance/ComplianceValidationTab';
import { ComplianceForm } from './aiwaf/compliance/ComplianceForm';
import { CustomRulesTab } from './aiwaf/custom-rules/CustomRulesTab';
import { CustomRuleForm } from './aiwaf/custom-rules/CustomRuleForm';
import { WebAccessControlTab } from './aiwaf/web-access-control/WebAccessControlTab';
import { WebsiteWhitelistForm } from './aiwaf/web-access-control/WebsiteWhitelistForm';
import { BotManagementTab } from './BotManagementTab';
import { BotManagementForm } from './BotManagementForm';
import { ProgrammableMitigationTab } from './ProgrammableMitigationTab';
import { WebAccessControlRuleForm } from './WebAccessControlRuleForm';
import { RateLimitingRuleForm } from './RateLimitingRuleForm';
import { BotOpenApiTab } from './BotOpenApiTab';
import { EmergencyMitigationTab } from './EmergencyMitigationTab';
import { ApiProtectionPage } from './api-protection/ApiProtectionPage';
import { ApiProtectionDiscoveryPage } from './api-protection/ApiProtectionDiscoveryPage';
import { ApiProtectionThreatDetectionPage } from './api-protection/ApiProtectionThreatDetectionPage';
import { ApiProtectionDataLossPage } from './api-protection/ApiProtectionDataLossPage';
import { ApiProtectionDataLossRulePage } from './api-protection/ApiProtectionDataLossRulePage';
import { ApiProtectionVulnerabilityPage } from './api-protection/ApiProtectionVulnerabilityPage';
import { ApiProtectionVulnerabilityRulePage } from './api-protection/ApiProtectionVulnerabilityRulePage';
import { WaapWebsiteDetail } from './website/WaapWebsiteDetail';
import { WafSettingsTab } from './aiwaf/WafSettingsTab';
import { DashboardTab } from './DashboardTab';
import { AnalyticsTab } from './AnalyticsTab';
import { QuickSetupTab } from './QuickSetupTab';
import { BusinessUsageTab } from './BusinessUsageTab';
import { LogsTab } from './LogsTab';
import { WaapSettingsPage } from './settings/WaapSettingsPage';

/**
 * Service-scoped WAAP (Eagle / SwiftFederation) console. WAAP is a standalone
 * service (per the product owner: "1 service khác") — it does NOT share the CDN
 * console and is not embedded in it. Mounted at /services/waap/:serviceId/*.
 *
 * The per-service navigation lives in the app's main ContextualSidebar (registered
 * via useSetContextualNav) — entering the console swaps the whole sidebar to its
 * scope with a "Back to Services" link. Each sub-page supplies its own page chrome.
 *
 * P00: every nav slug routes to a WaapPlaceholder so the console is fully
 * navigable. Feature tasks (P01+) swap individual routes for their real tabs.
 */
export function WaapConsole() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { serviceId } = useParams<{ serviceId: string }>();
  const base = `/services/waap/${serviceId}`;

  // Every WAAP feature is shown in the sidebar and fully browsable regardless of
  // plan. Plan gating happens at the ACTION level (create / enable) inside each
  // tab — a gated action opens an upgrade popup (see useWaapUpgradeGate) instead
  // of being blocked in the nav.
  useSetContextualNav(() => {
    const items: ContextualNavEntry[] = WAAP_NAV.map((entry) => {
      const icon = createElement(entry.icon, { size: 16 });
      if (entry.children) {
        return {
          id: waapHref(base, entry.slug),
          label: entry.label,
          icon,
          items: entry.children.map((child) => {
            const href = waapHref(base, child.slug);
            return { id: href, label: child.label, onSelect: () => navigate(href) };
          }),
        };
      }
      const href = waapHref(base, entry.slug);
      return { id: href, label: entry.label, icon, onSelect: () => navigate(href) };
    });
    const sections: ContextualNavSection[] = [{ items }];
    return {
      sections,
      activeId: waapActiveId(base, pathname),
      back: { label: WAAP_BACK.label, onSelect: () => navigate(WAAP_BACK.path) },
    };
  }, [base, pathname, navigate]);

  return (
    <WaapWebsiteProvider serviceId={serviceId ?? ''}>
      <Routes>
      {/* Dashboard (root) */}
      <Route index element={<DashboardTab />} />
      <Route path="onboarding" element={<QuickSetupTab />} />
      <Route path="websites" element={<WebsitesTab />} />
      <Route path="websites/new" element={<AddWebsite />} />
      {/* Per-website config detail (Configuration / Origin / … / Redirection /
          SSL / CORS / Purge / Prefetch) — the vendor's website tab layout. */}
      <Route path="websites/:domainId/*" element={<WaapWebsiteDetail />} />

      {/* AI-WAF */}
      <Route path="aiwaf/general" element={<GeneralProtectionTab />} />
      <Route path="aiwaf/custom-policy/new" element={<CustomPolicyForm />} />
      <Route path="aiwaf/custom-policy/:policyId" element={<CustomPolicyForm />} />
      <Route path="aiwaf/advanced" element={<AdvancedProtectionTab />} />
      <Route path="aiwaf/advanced/anti-leech/new" element={<AntiLeechForm />} />
      <Route path="aiwaf/advanced/anti-leech/:id" element={<AntiLeechForm />} />
      <Route path="aiwaf/advanced/brute-force/new" element={<BruteForceForm />} />
      <Route path="aiwaf/advanced/brute-force/:id" element={<BruteForceForm />} />
      <Route path="aiwaf/advanced/cc-protection/new" element={<CCProtectionForm />} />
      <Route path="aiwaf/advanced/cc-protection/:id" element={<CCProtectionForm />} />
      <Route path="aiwaf/advanced/xml-protection/new" element={<XMLProtectionForm />} />
      <Route path="aiwaf/advanced/xml-protection/:id" element={<XMLProtectionForm />} />
      <Route path="aiwaf/advanced/weak-password/new" element={<WeakPasswordForm />} />
      <Route path="aiwaf/advanced/weak-password/:id" element={<WeakPasswordForm />} />
      <Route path="aiwaf/advanced/vulnerability-scanning/new" element={<VulnerabilityScanningForm />} />
      <Route path="aiwaf/advanced/vulnerability-scanning/:id" element={<VulnerabilityScanningForm />} />
      <Route path="aiwaf/advanced/honeypot/new" element={<HoneypotForm />} />
      <Route path="aiwaf/advanced/honeypot/:id" element={<HoneypotForm />} />
      <Route path="aiwaf/advanced/cookie/new" element={<CookieForm />} />
      <Route path="aiwaf/advanced/cookie/:id" element={<CookieForm />} />
      <Route path="aiwaf/advanced/illegal-download/new" element={<IllegalDownloadForm />} />
      <Route path="aiwaf/advanced/illegal-download/:id" element={<IllegalDownloadForm />} />
      <Route path="aiwaf/advanced/page-defacement/:siteId" element={<PageDefacementForm />} />
      <Route path="aiwaf/advanced/csrf/:siteId" element={<CsrfForm />} />
      <Route path="aiwaf/compliance" element={<ComplianceValidationTab />} />
      <Route path="aiwaf/compliance/new" element={<ComplianceForm />} />
      <Route path="aiwaf/compliance/:id" element={<ComplianceForm />} />
      <Route path="aiwaf/custom-rules" element={<CustomRulesTab />} />
      <Route path="aiwaf/custom-rules/new" element={<CustomRuleForm />} />
      <Route path="aiwaf/custom-rules/:id" element={<CustomRuleForm />} />
      <Route path="aiwaf/web-access-control" element={<WebAccessControlTab />} />
      <Route path="aiwaf/web-access-control/:siteId" element={<WebsiteWhitelistForm />} />
      <Route path="aiwaf/settings" element={<WafSettingsTab />} />

      {/* Other product areas. Plan gating is applied at the action level inside
          each tab (create / enable → upgrade popup), so the pages render freely. */}
      <Route path="api-protection" element={<ApiProtectionPage />} />
      <Route path="api-protection/discovery" element={<ApiProtectionDiscoveryPage />} />
      <Route path="api-protection/threat-detection" element={<ApiProtectionThreatDetectionPage />} />
      <Route path="api-protection/data-loss" element={<ApiProtectionDataLossPage />} />
      <Route path="api-protection/data-loss/:ruleId" element={<ApiProtectionDataLossRulePage />} />
      <Route path="api-protection/vulnerability" element={<ApiProtectionVulnerabilityPage />} />
      <Route path="api-protection/vulnerability/:ruleId" element={<ApiProtectionVulnerabilityRulePage />} />
      <Route path="bot-management" element={<BotManagementTab />} />
      <Route path="bot-management/new" element={<BotManagementForm />} />
      <Route path="bot-management/:id" element={<BotManagementForm />} />
      <Route path="bot-openapi" element={<BotOpenApiTab />} />
      <Route path="programmable-mitigation" element={<ProgrammableMitigationTab />} />
      <Route
        path="programmable-mitigation/web-access-control/new"
        element={<WebAccessControlRuleForm />}
      />
      <Route
        path="programmable-mitigation/web-access-control/:ruleId"
        element={<WebAccessControlRuleForm />}
      />
      <Route path="programmable-mitigation/rate-limiting/new" element={<RateLimitingRuleForm />} />
      <Route
        path="programmable-mitigation/rate-limiting/:ruleId"
        element={<RateLimitingRuleForm />}
      />
      <Route path="emergency-mitigation" element={<EmergencyMitigationTab />} />
      <Route path="analytics" element={<AnalyticsTab />} />
      <Route path="usage" element={<BusinessUsageTab />} />
      <Route path="logs" element={<LogsTab />} />
      <Route path="settings" element={<WaapSettingsPage />} />

      {/* Unknown sub-path → Dashboard */}
      <Route path="*" element={<DashboardTab />} />
      </Routes>
    </WaapWebsiteProvider>
  );
}
